import { Node, mergeAttributes, JSONContent } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import { storeToRefs } from 'pinia'

import { useEditorStore } from '@/stores'
import { DynamicNodeAttrs, ForBlockAttrs } from '@/types'

import { contentElementFiltered, handleKeyDownForBlocks } from './helpers'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    forBlock: {
      insertForBlock: (attrs: ForBlockAttrs, content?: JSONContent[] | undefined) => ReturnType
      openForBlockDialog: (attrs?: ForBlockAttrs, callback?: (attrs: DynamicNodeAttrs) => void) => ReturnType
    }
  }
}

const forBlockName = 'forBlock'

function contentElement(dom: HTMLElement) {
  return contentElementFiltered(dom, 'data-for-edit')
}

export const ForBlock = Node.create({
  name: forBlockName,

  group: 'block',
  content: 'block+',
  atom: false,
  draggable: false,

  addAttributes() {
    return {
      arrayName: {
        default: '',
        parseHTML: el => el.getAttribute('data-array-name') || '',
        renderHTML: attrs => (attrs.arrayName ? { 'data-array-name': attrs.arrayName } : {}),
      },
      arrayField: {
        default: '',
        parseHTML: el => el.getAttribute('data-array-field') || '',
        renderHTML: attrs => (attrs.arrayField ? { 'data-array-field': attrs.arrayField } : {}),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-for-block="true"]',
        priority: 100,
        contentElement,
      },
      {
        tag: 'div[data-for-block]',
        priority: 50,
        contentElement,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const arrayName = node.attrs.arrayName || '[]'
    const arrayField = node.attrs.arrayField || ''
    const arrayVariable = 'item'

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-for-block': 'true',
        'data-array-name': arrayName,
        'data-array-field': arrayField,
      }),
      // header handle (clicking opens dialog)
      [
        'div',
        {
          'data-for-edit': 'true',
          contenteditable: 'false',
        },
        `{% for ${arrayVariable} in ${arrayName} %}`,
      ],
      // content hole wrapper
      ['div', 0],
      // footer handle (clicking opens dialog)
      [
        'div',
        {
          'data-for-edit': 'true',
          contenteditable: 'false',
        },
        '{% endfor %}',
      ],
    ]
  },

  addCommands() {
    const editor = this.editor
    return {
      insertForBlock:
        (attrs, content = []) =>
        ({ chain }) => {
          const arrayVariable = 'item'
          const arrayField = attrs.arrayField || ''

          let text = arrayVariable
          if (arrayField) {
            text = arrayField === 'loop.index' ? 'loop.index' : `${arrayVariable}.${arrayField}`
          }

          const defaultContent: JSONContent[] = content.length
            ? content
            : [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: `{{ ${text} }}` }],
                },
              ]

          return chain()
            .insertContent({
              type: this.name,
              attrs,
              content: defaultContent,
            })
            .run()
        },

      openForBlockDialog: (attrs, callback) => () => {
        const editorStore = useEditorStore()
        const { dynamicDialogType, dynamicDialogAttrs, dynamicDialogCallback } = storeToRefs(editorStore)
        dynamicDialogType.value = 'for'
        dynamicDialogAttrs.value = attrs
        dynamicDialogCallback.value = callback ?? (attrs => editor?.commands.insertForBlock(attrs as ForBlockAttrs))
        return true
      },
    }
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        props: {
          // signature: (view, pos, node, nodePos, event, direct)
          handleClickOn(view, _pos, node, nodePos, event) {
            if (node.type.name !== forBlockName || !editor.isEditable) return false
            const target = event?.target as HTMLElement | null
            if (!target) return false

            const handle = target.closest('[data-for-edit="true"]')
            const block = target.closest('[data-for-block="true"]')
            if (!handle || !block) return false

            const oldAttrs = node.attrs as ForBlockAttrs

            editor?.commands.openForBlockDialog(oldAttrs, newAttrs => {
              const tr = view.state.tr

              // Check if the field changed
              const fieldChanged = oldAttrs.arrayField !== newAttrs.arrayField

              if (fieldChanged) {
                // Regenerate content with new field
                const arrayVariable = 'item'
                const arrayField = (newAttrs as ForBlockAttrs).arrayField || ''

                let text = arrayVariable
                if (arrayField) {
                  text = arrayField === 'loop.index' ? 'loop.index' : `${arrayVariable}.${arrayField}`
                }

                const newContent = view.state.schema.nodes.paragraph.create(
                  null,
                  view.state.schema.text(`{{ ${text} }}`)
                )

                // Replace the entire node with updated attrs and content
                tr.replaceWith(
                  nodePos,
                  nodePos + node.nodeSize,
                  view.state.schema.nodes.forBlock.create(newAttrs as ForBlockAttrs, newContent)
                )
              } else {
                // Just update attrs
                tr.setNodeMarkup(nodePos, undefined, newAttrs)
              }

              view.dispatch(tr)
            })
            return true
          },
          handleKeyDown(view, event) {
            return handleKeyDownForBlocks(view, event)
          },
        },
      }),
    ]
  },
})
